import { getGruposAgencias } from "../../constants/gruposAgencias";
import { useAuthContext } from "../../context/auth";

/**
 * Dropdown de agencias con grupos (holdings) ordenados alfabéticamente
 * @param {Object} props
 * @param {string} props.value - Valor seleccionado (id de agencia o "grupo-{index}")
 * @param {function} props.onChange - Callback cuando cambia la selección
 * @param {Array} props.intermediarios - Lista de intermediarios/agencias
 * @param {string} props.placeholder - Texto por defecto (default: "Agencia")
 * @param {string} props.className - Clases CSS adicionales
 * @param {boolean} props.includeGrupos - Incluir grupos/holdings (default: true)
 */
export const AgenciasDropdown = ({
  value,
  onChange,
  intermediarios = [],
  placeholder = "Agencia",
  className = "",
  includeGrupos = true,
}) => {
  const { pais } = useAuthContext();
  const gruposAgencias = getGruposAgencias(pais);

  const getOptions = () => {
    let options = [];

    // Nombres de grupos para evitar duplicados
    const nombresGrupos = includeGrupos ? gruposAgencias.map((g) => g.nombre.toUpperCase()) : [];

    // Agregar grupos si está habilitado
    if (includeGrupos) {
      const grupoOptions = gruposAgencias
        .filter((grupo) => intermediarios.some((i) => grupo.ids.includes(i.id)))
        .map((grupo, index) => ({
          value: `grupo-${index}`,
          label: grupo.nombre,
          sortKey: grupo.nombre.toLowerCase(),
          isGrupo: true,
          ids: grupo.ids,
        }));
      options = [...options, ...grupoOptions];
    }

    // Agregar agencias individuales (excluyendo las que tienen nombre exacto de un grupo)
    const agenciaOptions = intermediarios
      .filter((intermediario) => !nombresGrupos.includes(intermediario.nombre.toUpperCase()))
      .map((intermediario) => ({
        value: intermediario.id.toString(),
        label: intermediario.nombre,
        sortKey: intermediario.nombre.toLowerCase(),
        isGrupo: false,
      }));
    options = [...options, ...agenciaOptions];

    // Ordenar alfabéticamente
    return options.sort((a, b) => a.sortKey.localeCompare(b.sortKey, "es"));
  };

  const options = getOptions();

  return (
    <select value={value} onChange={onChange} className={className}>
      <option value="">{placeholder}</option>
      {options.map((option, index) => (
        <option key={`agencia-${option.value}-${index}`} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

/**
 * Helper para filtrar por valor seleccionado del dropdown
 * @param {string} selectedValue - Valor del dropdown (id o "grupo-{index}")
 * @param {number} agenciaId - ID de la agencia a verificar
 * @param {string} pais - País actual
 * @returns {boolean} - true si la agencia coincide con la selección
 */
export const matchAgenciaFilter = (selectedValue, agenciaId, pais) => {
  if (!selectedValue) return true;

  if (selectedValue.startsWith("grupo-")) {
    const grupoIndex = parseInt(selectedValue.replace("grupo-", ""), 10);
    const grupos = getGruposAgencias(pais);
    const grupo = grupos[grupoIndex];
    return grupo?.ids.includes(agenciaId);
  }

  return agenciaId?.toString() === selectedValue;
};
